import { Table, Tag } from 'antd';
import { format, parse } from 'date-fns';
import type { ColumnsType } from 'antd/es/table';
import { useEffect } from 'react';
import { useStoreState, useStoreActions } from '../../../store';
import type { CarrierSelectionTableSummary } from '../../../store/simulation/types';

export interface HistoricalShipmentTableProps {
  data: CarrierSelectionTableSummary[];
  samplingPercentage: number;
}

// shippedDate carries a date with no meaningful time component. `new Date('2026-07-30')` would
// parse it as UTC midnight and format as the previous day for any viewer west of UTC, so parse the
// date parts in the viewer's own timezone to keep this column agreeing with the Date column.
export const formatShippedDayOfWeek = (shippedDate?: string) =>
  shippedDate ? format(parse(shippedDate.split('T')[0], 'yyyy-MM-dd', new Date()), 'EEEE') : '?';

export const HistoricalShipmentTable = ({ data, samplingPercentage }: HistoricalShipmentTableProps) => {
  const contexts = useStoreState((state) => state.FP_fulfillmentContext.fulfillmentContextTableSummaries);
  const fetchContexts = useStoreActions((actions) => actions.FP_fulfillmentContext.fetchFulfillmentContextDetails);
  const originNameMap = contexts.reduce((prev: Record<string, string>, val) => {
    prev[val.context.fulfillmentContextId] = val.context?.name;
    return prev;
  }, {});

  useEffect(() => {
    fetchContexts();
  }, [fetchContexts]);

  const columns: ColumnsType<CarrierSelectionTableSummary> = [
    {
      title: 'Created Date',
      key: 'shippedDate',
      render: (row) => {
        return row.shippedDate ? row.shippedDate?.split('T')[0] : '?';
      },
    },
    {
      title: 'Day of Week',
      key: 'dayOfWeek',
      render: (row) => {
        return formatShippedDayOfWeek(row.shippedDate);
      },
    },
    {
      title: 'Shipments',
      key: 'carrierSelectionCount',
      render: (row) => {
        return row.carrierSelectionCount.toLocaleString();
      },
    },
    {
      title: 'Sampled Count',
      key: 'sampledSelectionCount',
      render: (row) => {
        return Math.round((row.carrierSelectionCount * samplingPercentage) / 100).toLocaleString();
      },
    },
    {
      title: 'Contexts',
      key: 'Contexts',
      render: (row) => {
        return (
          <div>
            {[...row.contexts].slice(0, 4).map((k: any) => (
              <Tag>{originNameMap[k]}</Tag>
            ))}
            {row.contexts.length - 4 > 0 ? <Tag>{row.contexts.length - 4} more </Tag> : null}
          </div>
        );
      },
    },
  ];

  return <Table columns={columns} dataSource={data} pagination={{ pageSize: 10 }} />;
};
